import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { useHistory } from 'react-router-dom';
import jwt_decode from 'jwt-decode';
import api from '../services/api';

interface SignInCredentials {
  email: string;
  password: string;
}

interface User {
  id: string;
  name: string;
  email: string;
}

interface AuthContextData {
  id_token: string;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
}

interface AuthState {
  id_token: string;
}

interface JwtData {
  exp: number;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC = ({ children }) => {
  const history = useHistory();
  const [data, setData] = useState<AuthState>(() => {
    const id_token = localStorage.getItem('@MyFinances:id_token');

    if (id_token) {
      const jwt_decoded = jwt_decode<JwtData>(id_token);
      const expiration = new Date(jwt_decoded.exp * 1000).getTime();
      const currentTime = new Date().getTime();

      // Clears local storage if id_token is expired
      if (expiration < currentTime) {
        localStorage.clear();
        return {} as AuthState;
      }
      api.defaults.headers.authorization = `Bearer ${id_token}`;

      return { id_token };
    }

    return {} as AuthState;
  });

  const signIn = useCallback(async ({ email, password }) => {
    const response = await api.post('login', { email, password });

    const { id_token } = response.data;

    localStorage.setItem('@MyFinances:id_token', id_token);

    api.defaults.headers.authorization = `Bearer ${id_token}`;

    setData({ id_token });
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@MyFinances:id_token');

    delete api.defaults.headers.authorization;

    setData({} as AuthState);
  }, []);

  useEffect(() => {
    const responseInterceptor = api.interceptors.response.use(
      response => response,
      error => {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          signOut();
          if (history.location.pathname !== '/') {
            history.push('/');
          }
        }

        return Promise.reject(error);
      },
    );

    return () => {
      api.interceptors.response.eject(responseInterceptor);
    };
  }, [signOut, history]);

  return (
    <AuthContext.Provider value={{ id_token: data.id_token, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  return context;
}
